/* eslint-disable react/no-array-index-key */
import { FC } from 'react';

import Icon from 'molecules/Icon';

import type { IconProps } from 'molecules/Icon/types';

interface StarsProps {
  rating: number | string;
  hideLabel?: boolean;
  wholeStarsOnly?: boolean;
}

/**
 * Determines the appropriate fractional star icon based on the given fraction.
 */
const getFractionalIcon = (fraction: number): string | null => {
  const adjustedFraction = Math.round(fraction * 10);

  if (adjustedFraction === 0) {
    return null;
  }
  if (adjustedFraction === 10) {
    return 'star';
  }

  return 'star-fraction';
};

const Stars: FC<StarsProps> = ({ rating, hideLabel = false, wholeStarsOnly = false }) => {
  let numericRating = typeof rating === 'string' ? parseFloat(rating.replace(',', '.')) : rating;

  if (isNaN(numericRating)) {
    numericRating = 0;
  }

  const cappedRating = Math.min(5, Math.max(0, numericRating));
  const displayRating = wholeStarsOnly ? Math.round(cappedRating) : cappedRating;

  const fullStars = Math.floor(displayRating);
  const fractionalPart = wholeStarsOnly ? 0 : displayRating - fullStars;
  const fractionalIcon = getFractionalIcon(fractionalPart);

  return (
    <div className="flex flex-row gap-2.5">
      <div className="flex flex-row gap-0.5">
        {Array(fullStars)
          .fill(null)
          .map((_, index) => (
            <Icon key={index} icon="star" size={24} iconColor="text-primary-300" />
          ))}
        {fractionalIcon && (
          <Icon
            key="fractional-star"
            icon={fractionalIcon as IconProps['icon']}
            size={24}
            iconColor="text-primary-300"
          />
        )}
      </div>
      {!hideLabel && <span className="text-md font-medium">{`${cappedRating.toFixed(1)} out of 5`}</span>}
    </div>
  );
};

export default Stars;
